/**
 * Datart
 *
 * Copyright 2021
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { ChartDataSectionType } from 'app/constants';
import {
  ChartConfig,
  ChartDataSectionField,
  ChartStyleConfig,
  LegendStyle,
} from 'app/types/ChartConfig';
import ChartDataSetDTO, { IChartDataSet } from 'app/types/ChartDataSet';
import {
  getColumnRenderName,
  getStyles,
  toFormattedValue,
  transformToDataSet,
} from 'app/utils/chartHelper';
import { init } from 'echarts';
import Chart from '../../../models/Chart';
import Config from './config';

interface RadarIndicator {
  name: string;
  max?: number;
}

class BasicRadarChart extends Chart {
  config = Config;
  chart: any = null;

  constructor(props?) {
    super(
      props?.id || 'radar',
      props?.name || 'viz.palette.graph.names.radarChart',
      props?.icon || 'radar',
    );
    this.meta.requirements = props?.requirements || [
      {
        group: [1, 1],
        aggregate: [1, 999],
      },
    ];
  }

  onMount(options, context): void {
    if (options.containerId === undefined || !context.document) {
      return;
    }

    this.chart = init(
      context.document.getElementById(options.containerId),
      'default',
    );
    this.mouseEvents?.forEach(event => {
      this.chart.on(event.name, event.callback);
    });
  }

  onUpdated(options): void {
    if (!options.dataset || !options.dataset.columns || !options.config) {
      return;
    }
    if (!this.isMatchRequirement(options.config)) {
      this.chart?.clear();
      return;
    }
    const newOptions = this.getOptions(options.dataset, options.config);
    this.chart?.setOption(Object.assign({}, newOptions), true);
  }

  onUnMount(): void {
    this.chart?.dispose();
  }

  onResize(opt: any, context): void {
    this.chart?.resize(context);
  }

  private getOptions(dataset: ChartDataSetDTO, config: ChartConfig) {
    const styleConfigs = config.styles || [];
    const dataConfigs = config.datas || [];

    const groupConfigs = dataConfigs
      .filter(c => c.type === ChartDataSectionType.GROUP)
      .flatMap(c => c.rows || []);
    // Only the "metrics" section drives radar polygons; "maxValue" is excluded.
    const aggregateConfigs = dataConfigs
      .filter(
        c => c.type === ChartDataSectionType.AGGREGATE && c.key === 'metrics',
      )
      .flatMap(c => c.rows || []);
    const maxConfigs = dataConfigs
      .filter(
        c => c.type === ChartDataSectionType.AGGREGATE && c.key === 'maxValue',
      )
      .flatMap(c => c.rows || []);

    const chartDataSet = transformToDataSet(
      dataset.rows,
      dataset.columns,
      dataConfigs,
    );

    const indicators = this.getIndicators(
      styleConfigs,
      chartDataSet,
      groupConfigs,
      aggregateConfigs,
      maxConfigs,
    );
    const series = this.getSeries(
      styleConfigs,
      chartDataSet,
      aggregateConfigs,
    );

    const nameFormatMap = aggregateConfigs.reduce((acc, cur) => {
      acc[getColumnRenderName(cur)] = cur.format;
      return acc;
    }, {} as Record<string, ChartDataSectionField['format']>);

    return {
      tooltip: {
        trigger: 'item',
        formatter: this.getTooltipFormatterFunc(indicators, nameFormatMap),
      },
      legend: this.getLegendStyle(
        styleConfigs,
        aggregateConfigs.map(getColumnRenderName),
      ),
      radar: this.getRadar(styleConfigs, indicators),
      series,
    };
  }

  private getIndicators(
    styleConfigs: ChartStyleConfig[],
    chartDataSet: IChartDataSet<string>,
    groupConfigs: ChartDataSectionField[],
    aggregateConfigs: ChartDataSectionField[],
    maxConfigs: ChartDataSectionField[],
  ): RadarIndicator[] {
    const [globalMax] = getStyles(styleConfigs, ['radar'], ['max']);
    const maxConfig = maxConfigs?.[0];

    return chartDataSet.map(row => {
      const name = groupConfigs.map(g => row.getCell(g)).join('-');
      let max: number | undefined;
      if (maxConfig) {
        // per-indicator max supplied by the data column
        max = Number(row.getCell(maxConfig));
      } else if (globalMax !== null && globalMax !== undefined) {
        max = Number(globalMax);
      } else {
        // radar requires a max per axis; derive it from the row's own metrics
        const rowValues = aggregateConfigs.map(a => Number(row.getCell(a)));
        max = Math.max(...rowValues);
      }
      return { name, max: Number.isFinite(max) ? max : undefined };
    });
  }

  private getSeries(
    styleConfigs: ChartStyleConfig[],
    chartDataSet: IChartDataSet<string>,
    aggregateConfigs: ChartDataSectionField[],
  ) {
    const [lineWidth, showSymbol, showAreaBackground] = getStyles(
      styleConfigs,
      ['graph'],
      ['lineWidth', 'showSymbol', 'showAreaBackground'],
    );
    const [showLabel, font] = getStyles(
      styleConfigs,
      ['label'],
      ['showLabel', 'font'],
    );

    const data = aggregateConfigs.map(aggConfig => {
      const color = aggConfig?.color?.start;
      return {
        name: getColumnRenderName(aggConfig),
        value: chartDataSet.map(row => row.getCell(aggConfig)),
        symbol: showSymbol ? 'circle' : 'none',
        lineStyle: { width: lineWidth, color },
        itemStyle: color ? { color } : undefined,
        areaStyle: showAreaBackground ? { opacity: 0.7 } : undefined,
        label: {
          show: showLabel,
          ...font,
          formatter: params =>
            toFormattedValue(params?.value, aggConfig?.format),
        },
      };
    });

    return [
      {
        type: 'radar',
        data,
      },
    ];
  }

  private getRadar(styleConfigs: ChartStyleConfig[], indicators: RadarIndicator[]) {
    const [shape, splitNumber, showSplitArea] = getStyles(
      styleConfigs,
      ['radar'],
      ['shape', 'splitNumber', 'showSplitArea'],
    );
    return {
      shape,
      splitNumber,
      indicator: indicators,
      splitArea: { show: showSplitArea ?? true },
    };
  }

  private getLegendStyle(
    styleConfigs: ChartStyleConfig[],
    seriesNames: string[],
  ): LegendStyle {
    const [show, type, font, position, selectAll] = getStyles(
      styleConfigs,
      ['legend'],
      ['showLegend', 'type', 'font', 'position', 'selectAll'],
    );
    let positions = {};
    let orient = '';

    switch (position) {
      case 'top':
        orient = 'horizontal';
        positions = { top: 8, left: 'center' };
        break;
      case 'bottom':
        orient = 'horizontal';
        positions = { bottom: 8, left: 'center' };
        break;
      case 'left':
        orient = 'vertical';
        positions = { left: 8, top: 'middle' };
        break;
      default:
        orient = 'vertical';
        positions = { right: 8, top: 'middle' };
        break;
    }

    const selected = seriesNames.reduce(
      (obj, name) => ({
        ...obj,
        [name]: selectAll,
      }),
      {},
    );

    return {
      ...positions,
      show,
      type,
      orient,
      selected,
      data: seriesNames,
      textStyle: font,
    };
  }

  private getTooltipFormatterFunc(
    indicators: RadarIndicator[],
    nameFormatMap: Record<string, ChartDataSectionField['format']>,
  ): (params) => string {
    return seriesParams => {
      const { name, value, marker } = seriesParams;
      const format = nameFormatMap[name];
      const lines = indicators.map(
        (indicator, index) =>
          `${indicator.name}: ${toFormattedValue(value?.[index], format)}`,
      );
      return `${marker || ''}${name}<br />${lines.join('<br />')}`;
    };
  }
}

export default BasicRadarChart;
